import React from "react"

const RATING = "rating"
const IGNORE = "ignore"
const TOTAL_LABEL = "Total"

const defaultLabel = (header) => header.replace(/^Participant:\s*/i, "").trim()

const sampleValues = (samples, index) =>
  samples
    .map((row) => row[index])
    .filter((v) => v !== null && v !== undefined && String(v).trim() !== "")
    .slice(0, 3)

const ColumnMapper = ({ headers, samples, fields, initialMapping, initialAddTotal, cancelUrl }) => {
  const [mapping, setMapping] = React.useState(() =>
    initialMapping.map((m) => ({ target: m.target, label: m.label || "" }))
  )
  const [addTotal, setAddTotal] = React.useState(initialAddTotal)

  const updateColumn = (index, changes) => {
    setMapping((prev) => prev.map((m, i) => (i === index ? { ...m, ...changes } : m)))
  }

  const changeTarget = (index, target) => {
    const changes = { target }
    if (target === RATING && !mapping[index].label) changes.label = defaultLabel(headers[index])
    updateColumn(index, changes)
  }

  const usedBy = {}
  mapping.forEach((m, i) => {
    if (m.target !== RATING && m.target !== IGNORE) usedBy[m.target] = i
  })

  const ratingLabels = mapping.filter((m) => m.target === RATING).map((m) => m.label.trim())
  const allLabels = addTotal ? [...ratingLabels, TOTAL_LABEL] : ratingLabels

  const problems = []
  const unchosen = mapping.filter((m) => m.target === "").length
  if (unchosen > 0) problems.push(`${unchosen} ${unchosen === 1 ? "column still needs" : "columns still need"} a choice.`)
  fields.forEach((f) => {
    if (f.required && usedBy[f.key] === undefined) problems.push(`${f.display} must be mapped to a column.`)
  })
  if (allLabels.some((l) => l === "")) problems.push("Every rating column needs a label.")
  const labelCounts = {}
  allLabels.forEach((l) => {
    if (l !== "") labelCounts[l] = (labelCounts[l] || 0) + 1
  })
  Object.keys(labelCounts).forEach((l) => {
    if (labelCounts[l] > 1) problems.push(`Rating label "${l}" is used more than once.`)
  })

  const previewColumns = [
    ...fields.filter((f) => usedBy[f.key] !== undefined).map((f) => ({ title: f.display, index: usedBy[f.key] })),
    ...mapping
      .map((m, i) => ({ m, i }))
      .filter(({ m }) => m.target === RATING)
      .map(({ m, i }) => ({ title: m.label || "(no label)", index: i })),
  ]
  const ratingIndexes = mapping.map((m, i) => (m.target === RATING ? i : null)).filter((i) => i !== null)
  const rowTotal = (row) =>
    ratingIndexes.map((i) => row[i]).filter((v) => typeof v === "number").reduce((a, b) => a + b, 0)

  return (
    <div className="text-start">
      {problems.length > 0 && (
        <div className="alert alert-warning">
          {problems.map((p) => (
            <div key={p}>{p}</div>
          ))}
        </div>
      )}

      <div className="table-responsive mb-3">
        <table className="column-mapper">
          <thead>
            <tr>
              <th className="column-mapper-num">#</th>
              <th>CSV column</th>
              <th>Sample values</th>
              <th>Maps to</th>
            </tr>
          </thead>
          <tbody>
            {headers.map((header, i) => {
              const m = mapping[i]
              const prefix = header.match(/^Participant:\s*/i)?.[0] || ""
              const rowClass = m.target === "" ? "is-unset" : m.target === IGNORE ? "is-ignored" : "is-mapped"
              return (
                <tr key={i} className={rowClass}>
                  <td className="column-mapper-num">{i + 1}</td>
                  <td>
                    <div className="column-mapper-header" title={header}>
                      {header ? (
                        <>
                          {prefix && <span className="column-mapper-prefix">{prefix}</span>}
                          {header.slice(prefix.length)}
                        </>
                      ) : (
                        <em className="column-mapper-prefix">(blank header)</em>
                      )}
                    </div>
                  </td>
                  <td>
                    {sampleValues(samples, i).map((v, j) => (
                      <span key={j} className="column-mapper-sample" title={String(v)}>{String(v)}</span>
                    ))}
                  </td>
                  <td className="column-mapper-target">
                    <select
                      name={`mapping[${i}][target]`}
                      className="form-select form-select-sm"
                      data-native-select="true"
                      value={m.target}
                      onChange={(e) => changeTarget(i, e.target.value)}
                    >
                      <option value="" disabled>-- Choose --</option>
                      <optgroup label="Player field">
                        {fields.map((f) => {
                          const owner = usedBy[f.key]
                          const taken = owner !== undefined && owner !== i
                          return (
                            <option key={f.key} value={f.key} disabled={taken}>
                              {f.display}
                              {f.required ? " *" : ""}
                              {taken ? ` (used by column ${owner + 1})` : ""}
                            </option>
                          )
                        })}
                      </optgroup>
                      <option value={RATING}>Rating</option>
                      <option value={IGNORE}>Ignore</option>
                    </select>
                    {m.target === RATING ? (
                      <input
                        type="text"
                        name={`mapping[${i}][label]`}
                        className="form-control form-control-sm"
                        placeholder="Rating label"
                        aria-label={`Rating label for column ${i + 1}`}
                        value={m.label}
                        onChange={(e) => updateColumn(i, { label: e.target.value })}
                      />
                    ) : (
                      <input type="hidden" name={`mapping[${i}][label]`} value="" />
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <div className="form-check mb-3">
        <input type="hidden" name="add_total" value="0" />
        <input
          type="checkbox"
          id="add_total"
          name="add_total"
          value="1"
          className="form-check-input"
          checked={addTotal}
          onChange={(e) => setAddTotal(e.target.checked)}
        />
        <label htmlFor="add_total" className="form-check-label">
          Add a "{TOTAL_LABEL}" rating (sum of numeric ratings)
        </label>
      </div>

      <h3 className="text-center">Preview</h3>
      <div className="table-responsive mb-3">
        <table className="column-mapper column-mapper-preview">
          <thead>
            <tr>
              {previewColumns.map((c) => (
                <th key={c.index}>{c.title}</th>
              ))}
              {addTotal && <th>{TOTAL_LABEL}</th>}
            </tr>
          </thead>
          <tbody>
            {samples.slice(0, 3).map((row, r) => (
              <tr key={r}>
                {previewColumns.map((c) => (
                  <td key={c.index}>{row[c.index] ?? ""}</td>
                ))}
                {addTotal && <td>{rowTotal(row)}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="actions text-center">
        <a href={cancelUrl} className="btn ccs-back-button me-1">Cancel</a>
        <button type="submit" className="btn btn-ccs-orange" disabled={problems.length > 0}>
          Import Players
        </button>
      </div>
    </div>
  )
}

export default ColumnMapper
